import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString } from "@icarius-localization/strings";
import { useState } from "react";
import { useDispatch } from "react-redux";
import * as yup from "yup";

const useHandleForm = (
    data,
    generateCallback,
    implementCallback,
    dataTypes,
    invalidDataCallback,
    openSelectorValidationError,
    testCallback,
    openMandatoryParametersError
) => {
    const isCreate = !Boolean(data);

    const createInitialData = () => {
        const getInitialFieldData = (fieldName) => {
            if (fieldName === "active") {
                return isCreate ? "Y" : data[fieldName];
            }

            if (fieldName === "selectors") {
                return isCreate ? "" : (data[fieldName] || []).join(",");
            }

            if (fieldName === "questions" || fieldName === "parameters") {
                return isCreate ? null : data[fieldName] || [];
            }

            return isCreate ? "" : data[fieldName];
        };

        const fieldNames = [
            "code",
            "name",
            "active",
            "request",
            "rol",
            "group",
            "sql",
            "selectors",
            "sqlContext",
            "comment",
            "questions",
            "parameters",
        ];

        return fieldNames.reduce((initialData, fieldName) => {
            return {
                ...initialData,
                [fieldName]: getInitialFieldData(fieldName),
            };
        }, {});
    };

    const dispatch = useDispatch();
    const [formData, setFormData] = useState(createInitialData());
    const [generationId, setGenerationId] = useState(null);
    const [summary, setSummary] = useState("");
    const [summaryIsError, setSummaryIsError] = useState(false);
    const [warnings, setWarnings] = useState([]);
    const [hasQueryContent, setHasQueryContent] = useState(false);
    const [promptHistory, setPromptHistory] = useState("");
    const [hasTested, setHasTested] = useState(false);
    const [testFilters, setTestFilters] = useState(null);
    const [testResult, setTestResult] = useState([]);
    const [isTesting, setIsTesting] = useState(false);

    const generateIsValid = async () => {
        const codeRegex = /^[A-Z]+[A-Z0-9]*$/;

        const schema = yup.object().shape({
            code: yup
                .string()
                .matches(codeRegex)
                .max(20)
                .required(),
            name: yup
                .string()
                .max(254)
                .required(),
            active: yup.string().required(),
            request: yup.string().required(),
            rol: yup.string().required(),
            group: yup
                .string()
                .max(100)
                .required(),
        });

        return await schema.isValid(formData).then((valid) => valid);
    };

    const implementIsValid = async () => {
        const codeRegex = /^[A-Z]+[A-Z0-9]*$/;

        const schema = yup.object().shape({
            code: yup
                .string()
                .matches(codeRegex)
                .max(20)
                .required(),
            name: yup
                .string()
                .max(254)
                .required(),
            active: yup.string().required(),
            request: yup.string(),
            sql: yup.string().required(),
            rol: yup.string().required(),
            group: yup
                .string()
                .max(100)
                .required(),
            selectors: yup.string().required(),
            sqlContext: yup.string(),
            comment: yup.string(),
            questions: yup
                .array()
                .of(
                    yup.object().shape({
                        order: yup.number().required(),
                        question: yup.string().required(),
                    })
                )
                .optional(),
            parameters: yup
                .array()
                .of(
                    yup.object().shape({
                        name: yup.string().required(),
                    })
                )
                .optional(),
        });

        return await schema.isValid(formData).then((valid) => valid);
    };

    const selectorsAreValid = () => {
        const selectors = formData.selectors.split(",").filter((selector) => selector.length > 0);
        const validSelectors = dataTypes.map((item) => item.key);

        return selectors.every((selector) => validSelectors.includes(selector));
    };

    const getParametersToSend = (includeTestValues = false) =>
        (formData.parameters || []).map((parameter, index) => {
            const { value, ...parameterData } = parameter;

            return {
                ...parameterData,
                paramNum: parseInt(parameter.paramNum) || index + 1,
                ...(includeTestValues ? { value: value ?? "" } : {}),
            };
        });

    const getQueryDefinitionToSend = () => {
        const queryDefinition = {
            code: formData.code,
            name: formData.name,
            active: formData.active,
            sql: formData.sql,
            selectors: formData.selectors.split(",").filter((selector) => selector.length > 0),
            rol: formData.rol,
            group: formData.group,
            sqlContext: formData.sqlContext,
            comment: formData.comment,
            questions: (formData.questions || []).map((question) => ({
                ...question,
                order: parseInt(question.order) || 0,
            })),
            parameters: getParametersToSend(),
            promptHistory,
        };

        return queryDefinition;
    };

    const getTestQueryDefinitionToSend = () => ({
        ...getQueryDefinitionToSend(),
        parameters: getParametersToSend(true),
    });

    const requiredParametersAreValid = () =>
        (formData.parameters || []).every(
            (parameter) => parameter.required !== "Y" || String(parameter.value ?? "").trim().length > 0
        );

    const generate = async () => {
        if (await generateIsValid()) {
            const payload = Boolean(generationId)
                ? {
                      generationId,
                      request: formData.request,
                      queryDefinition: getQueryDefinitionToSend(),
                  }
                : {
                      request: formData.request,
                      code: formData.code,
                      name: formData.name,
                      active: formData.active,
                      rol: formData.rol,
                      group: formData.group,
                  };

            if (!isCreate) payload.originCode = data.code;

            const response = await generateCallback(payload);

            // 422 INVALID_DATA: el generationId enviado ya no es válido (por ejemplo,
            // expiró). Se descarta para que el próximo "Generar con IA" arranque una
            // generación nueva con los datos de cabecera, conservando lo que el
            // usuario tenga cargado en el formulario.
            if (response?.response?.data?.status === "INVALID_DATA") {
                setGenerationId(null);
                setSummary("");
                setSummaryIsError(false);
                setWarnings([]);
                return false;
            }

            if (response?.data?.status === "OK") {
                const generationData = response.data.data || {};
                const queryDefinition = generationData.queryDefinition;

                setGenerationId(generationData.generationId || null);
                setSummary(generationData.summary || "");
                setSummaryIsError(!String(queryDefinition?.sql ?? "").trim());
                setWarnings(generationData.warnings || []);
                if (typeof queryDefinition?.promptHistory === "string") {
                    setPromptHistory(queryDefinition.promptHistory);
                } else if (typeof generationData.promptHistory === "string") {
                    setPromptHistory(generationData.promptHistory);
                }

                if (queryDefinition != null) {
                    setHasQueryContent(true);
                    setFormData((prev) => ({
                        ...prev,
                        code: queryDefinition.code ?? prev.code,
                        name: queryDefinition.name ?? prev.name,
                        active: queryDefinition.active ?? prev.active,
                        sql: queryDefinition.sql ?? prev.sql ?? "",
                        selectors: Array.isArray(queryDefinition.selectors)
                            ? queryDefinition.selectors.join(",")
                            : queryDefinition.selectors ?? prev.selectors ?? "",
                        rol: queryDefinition.rol ?? prev.rol,
                        group: queryDefinition.group ?? prev.group,
                        sqlContext: queryDefinition.sqlContext ?? prev.sqlContext ?? "",
                        comment: queryDefinition.comment ?? prev.comment ?? "",
                        questions: Array.isArray(queryDefinition.questions)
                            ? queryDefinition.questions
                            : prev.questions || [],
                        parameters: Array.isArray(queryDefinition.parameters)
                            ? queryDefinition.parameters
                            : prev.parameters || [],
                        request: "",
                    }));
                }
                return true;
            }
            return false;
        } else {
            invalidDataCallback();
            return false;
        }
    };

    const implement = async () => {
        if (await implementIsValid()) {
            if (selectorsAreValid()) {
                const payload = getQueryDefinitionToSend();
                if (!isCreate) payload.replaceQuery = true;

                const response = await implementCallback(payload);
                return response?.status === 200;
            }
            openSelectorValidationError();
            return false;
        } else {
            invalidDataCallback();
            return false;
        }
    };

    const test = async () => {
        if (!(await implementIsValid())) {
            invalidDataCallback();
            return false;
        }

        if (!selectorsAreValid()) {
            openSelectorValidationError();
            return false;
        }

        if (!requiredParametersAreValid()) {
            openMandatoryParametersError();
            return false;
        }

        setIsTesting(true);

        try {
            const response = await testCallback(getTestQueryDefinitionToSend());

            if (response?.response?.data?.status === "INVALID_VALUE") {
                dispatch(openSnackbarAction({ msg: getLocalizedString("invalidParamValues"), severity: "error" }));
                return false;
            }

            if (response?.data?.status !== "OK") return false;

            setTestFilters(response.data.filters || null);
            setTestResult(Array.isArray(response.data.result) ? response.data.result : []);
            setHasTested(true);
            return true;
        } finally {
            setIsTesting(false);
        }
    };

    const formatValue = (value, fieldName) => {
        if (fieldName === "code") {
            return value.length > 0 ? value.replace(/\s/g, "").toUpperCase() : "";
        }

        if (fieldName === "selectors") {
            if (value.length === 0) return "";
            if (value.charAt(0) === ",") {
                return value.substring(1);
            }
        }

        return value;
    };

    const setFormValue = (value, fieldName) => {
        setFormData({
            ...formData,
            [fieldName]: formatValue(value, fieldName),
        });
    };

    return {
        isCreate,
        formData,
        setFormValue,
        generate,
        implement,
        test,
        hasQueryContent,
        summary,
        summaryIsError,
        warnings,
        promptHistory,
        hasTested,
        testFilters,
        testResult,
        isTesting,
    };
};

export default useHandleForm;
